import SideModal from "components/SideModal";
import { useToast } from "contexts/ToastContext";
import { ChangeEvent, useRef, useState } from "react";
import { addInvestigation } from "services/advocateComplaintService";
import { AdvocateComplaints } from "types";
import { LoadingIcon } from "utils/constant";
import {
  INVESTIGATION_STATUS,
  PROSECUTOR_DECISION_TYPE,
  PROSECUTOR_DECISION_TYPE2,
} from "utils/constantValues";
import { useTranslation } from "contexts/i18nContext";
import "../../complaintRequest/styles/AddEvidenceModal.css";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  complaintData: AdvocateComplaints;
  onRefresh: any;
}

export default function AddDecisionModal({
  isOpen,
  setIsOpen,
  complaintData,
  onRefresh,
}: DecisionModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState({
    decision_type: PROSECUTOR_DECISION_TYPE?.FRAME_CHARGE,
    decision_summary: "",
    decision_date: "",
    status: INVESTIGATION_STATUS?.PENDING,
    complaint: null,
    files: [] as File[],
    charge_summary: "",
    artcicle_of_charge: "",
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormState((prev) => ({
        ...prev,
        files: [...prev.files, ...Array.from(e.target.files as FileList)],
      }));
    }
  };

  const removeFile = (index: number) => {
    setFormState((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      PROSECUTOR_DECISION_TYPE2.CLOSE_THE_CASE === formState?.decision_type &&
      !formState.decision_date
    ) {
      showToast(t("selectDecisionDate"), "error");
      return;
    }

    if (
      PROSECUTOR_DECISION_TYPE2.CLOSE_THE_CASE === formState?.decision_type &&
      !formState.decision_summary.trim()
    ) {
      showToast(t("provideDecisionSummary"), "error");
      return;
    }

    setLoading(true);

    const data = new FormData();
    if (PROSECUTOR_DECISION_TYPE2.FRAME_CHARGE === formState?.decision_type)
      data.append("charge_documents", formState?.files[0] || "");
    else
      data.append("prosecutor_decision_documents", formState?.files[0] || "");

    data.append("prosecutor_decision_type", formState.decision_type);
    data.append("investigation_start_date", formState.decision_date);
    data.append("prosecutor_decision_date", formState.decision_date);
    data.append("prosecutor_decision_summary", formState.decision_summary);

    data.append(
      "status",
      PROSECUTOR_DECISION_TYPE2.FRAME_CHARGE === formState?.decision_type
        ? INVESTIGATION_STATUS.BACK_TO_PROSECUTOR
        : formState.status,
    );

    data.append(
      "charge_documents",
      PROSECUTOR_DECISION_TYPE2?.FRAME_CHARGE === formState.decision_type
        ? formState?.charge_summary
        : "",
    );
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );
    data.append("complaint", String(complaintData?.uu_id));

    const isFormData = true;

    try {
      await addInvestigation(data, isFormData);

      onRefresh();
      setIsOpen(false);
      showToast(t("decisionAddedSuccessfully"), "success");
      setFormState({
        decision_type: PROSECUTOR_DECISION_TYPE?.FRAME_CHARGE,
        decision_summary: "",
        decision_date: "",
        status: INVESTIGATION_STATUS.PENDING,
        complaint: null,
        files: [] as File[],
        charge_summary: "",
        artcicle_of_charge: "",
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("failedToAddDecision"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("addProsecutorDecision")}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Complaint Info */}
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-red-800 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {t("caseDetails")}
              </h3>
              <p className="text-xs text-gray-600">
                {complaintData?.complaint_number} •{" "}
                {complaintData?.accused_name}
              </p>
            </div>
          </div>
        </div>

        {/* Decision Form */}
        <section className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-gray-700">
                {t("decisionType")}
              </label>
            </div>
            <select
              required
              className="w-full border border-gray-300 bg-white rounded-lg px-4 py-3 text-sm focus:border-red-800 focus:ring-2 focus:ring-red-100 transition-colors"
              name="decision_type"
              value={formState.decision_type}
              onChange={handleChange}
            >
              {Object.values(PROSECUTOR_DECISION_TYPE2)?.map((list) => (
                <option key={list} value={list}>
                  {list}
                </option>
              ))}
            </select>
          </div>

          {PROSECUTOR_DECISION_TYPE2?.CLOSE_THE_CASE ===
            formState.decision_type && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">
                  {t("decisionDate")}
                </label>
              </div>
              <div className="relative">
                <input
                  required
                  className="w-full border border-gray-300 bg-white rounded-lg px-4 py-3 text-sm focus:border-red-800 focus:ring-2 focus:ring-red-100 transition-colors"
                  type="date"
                  name="decision_date"
                  value={formState.decision_date}
                  onChange={handleChange}
                />
                <svg
                  className="absolute right-3 top-3 w-5 h-5 text-gray-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
          )}

          {PROSECUTOR_DECISION_TYPE2?.CLOSE_THE_CASE ===
            formState.decision_type && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-gray-700">
                  {t("decisionSummary")}
                </label>
              </div>
              <textarea
                rows={4}
                name="decision_summary"
                value={formState.decision_summary}
                onChange={handleChange}
                placeholder={t("decisionSummaryPlaceholder")}
                className="w-full border border-gray-300 bg-white rounded-lg px-4 py-3 text-sm focus:border-red-800 focus:ring-2 focus:ring-red-100 transition-colors resize-none"
              />
              <p className="text-xs text-gray-500 mt-2">
                {t("characterCount")}: {formState.decision_summary.length}/5000
              </p>
            </div>
          )}

          {PROSECUTOR_DECISION_TYPE2?.FRAME_CHARGE ===
            formState.decision_type && (
            <div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700">
                    {t("chargeSummary")}
                  </label>
                </div>
                <textarea
                  rows={4}
                  name="charge_summary"
                  value={formState.charge_summary}
                  onChange={handleChange}
                  placeholder={t("chargeSummaryPlaceholder")}
                  className="w-full border border-gray-300 bg-white rounded-lg px-4 py-3 text-sm focus:border-red-800 focus:ring-2 focus:ring-red-100 transition-colors resize-none"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {t("characterCount")}: {formState.decision_summary.length}
                  /5000
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-gray-700">
                    {t("articleOfCharge")}
                  </label>
                </div>
                <textarea
                  rows={4}
                  name="artcicle_of_charge"
                  value={formState.artcicle_of_charge}
                  onChange={handleChange}
                  placeholder={t("articleOfChargePlaceholder")}
                  className="w-full border border-gray-300 bg-white rounded-lg px-4 py-3 text-sm focus:border-red-800 focus:ring-2 focus:ring-red-100 transition-colors resize-none"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {t("characterCount")}: {formState.decision_summary.length}
                  /5000
                </p>
              </div>

              <section className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700">
                    {t("chargeFile")}
                  </label>
                  <span className="text-xs text-gray-500">*</span>
                </div>

                <div
                  onClick={triggerFileInput}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-red-800 hover:bg-red-50 transition-colors group"
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    multiple
                    style={{ display: "none" }}
                  />
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                    <svg
                      className="w-6 h-6 text-red-800"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    {t("uploadDecisionDocuments")}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t("clickToUploadOrDrag")}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">{t("fileTypes")}</p>
                </div>

                {/* File List */}
                {formState.files.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">
                        {t("selectedFiles")} ({formState.files.length})
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setFormState((prev) => ({ ...prev, files: [] }))
                        }
                        className="text-xs text-red-800 hover:text-red-700 hover:underline"
                      >
                        {t("clearAll")}
                      </button>
                    </div>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {formState.files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded bg-red-50 flex items-center justify-center">
                              <svg
                                className="w-4 h-4 text-red-800"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                                {file.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="p-1 hover:bg-gray-200 rounded"
                          >
                            <svg
                              className="w-5 h-5 text-gray-400 hover:text-red-800"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </div>
          )}
        </section>

        {/* File Upload Section */}
        {PROSECUTOR_DECISION_TYPE2?.CLOSE_THE_CASE ===
          formState.decision_type && (
          <section className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-700">
                {t("supportingDocuments")}
              </label>
              <span className="text-xs text-gray-500">{t("optional")}</span>
            </div>

            <div
              onClick={triggerFileInput}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-red-800 hover:bg-red-50 transition-colors group"
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                multiple
                style={{ display: "none" }}
              />
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center group-hover:bg-red-100 transition-colors">
                <svg
                  className="w-6 h-6 text-red-800"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                {t("uploadDecisionDocuments")}
              </p>
              <p className="text-xs text-gray-500">
                {t("clickToUploadOrDrag")}
              </p>
              <p className="text-xs text-gray-400 mt-2">{t("fileTypes")}</p>
            </div>

            {/* File List */}
            {formState.files.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">
                    {t("selectedFiles")} ({formState.files.length})
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setFormState((prev) => ({ ...prev, files: [] }))
                    }
                    className="text-xs text-red-800 hover:text-red-700 hover:underline"
                  >
                    {t("clearAll")}
                  </button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {formState.files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-red-50 flex items-center justify-center">
                          <svg
                            className="w-4 h-4 text-red-800"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-xs">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        <svg
                          className="w-5 h-5 text-gray-400 hover:text-red-800"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Action Buttons */}
        <div className="sticky bottom-0 pt-6 bg-white border-t border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {/* <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg> */}
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              {t("cancel")}
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-red-800 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={
                PROSECUTOR_DECISION_TYPE2?.CLOSE_THE_CASE ===
                formState.decision_type
                  ? loading ||
                    !formState.decision_date ||
                    !formState.decision_summary
                  : loading ||
                    !formState.charge_summary ||
                    !formState.artcicle_of_charge ||
                    formState.files.length === 0
              }
            >
              {(
                PROSECUTOR_DECISION_TYPE2?.CLOSE_THE_CASE ===
                formState.decision_type
                  ? loading ||
                    !formState.decision_date ||
                    !formState.decision_summary
                  : loading ||
                    !formState.charge_summary ||
                    !formState.artcicle_of_charge ||
                    formState.files.length === 0
              ) ? (
                <div className="flex items-center justify-center gap-2">
                  {LoadingIcon}
                  <span>{t("addingDecision")}</span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  <span>{t("addProsecutorDecision")}</span>
                </div>
              )}
            </button>
          </div>
        </div>
      </form>
    </SideModal>
  );
}
